import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LOGO from '../assets/img/logo.png';
import {
  getMobilePlatform,
  isStandaloneApp,
  MOBILE_APP_SCHEME,
  MOBILE_COMPANION_SCHEME,
  STORE_URLS,
} from '../helpers/mobileApp';
import { getMagicLink } from '../redux/magicLinkService';
import { capture } from '../services/analytics';

// Re-show the banner this many days after the user dismisses it.
const DISMISS_KEY = 'neo_app_banner_dismissed_until_v2';
const DISMISS_DAYS = 7;
const STORE_FALLBACK_DELAY_MS = 1800;
const STORE_FALLBACK_MAX_DRIFT_MS = 1000;

const isDismissed = () => {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) || 0) > Date.now();
  } catch (e) {
    return false;
  }
};

/**
 * Banner shown on mobile browsers inviting the user to open the mobile app
 * through a magic link.
 *
 * - Not installed  -> the Universal Link's landing page redirects to the store.
 * - Companion session -> opens the app's companion entry point.
 * - Installed, not logged in on web -> opens the app (logged out).
 * - Installed, logged in on web -> backend mints a one-time magic link and the
 *   app logs the user in automatically.
 */
const MobileAppBanner = () => {
  const token = useSelector(state => state.user.access);
  const companionToken = useSelector(state => state.companion.token);
  const isAuth = !!token && token !== '';
  const isCompanion = !isAuth && Boolean(companionToken);
  const platform = getMobilePlatform();
  const shouldShow = platform !== null;

  const [visible, setVisible] = useState(() => shouldShow && !isStandaloneApp() && !isDismissed());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shouldShow || isStandaloneApp()) {
      setVisible(false);
      return;
    }
    if (!isDismissed()) setVisible(true);
  }, [shouldShow]);

  useEffect(() => {
    if (visible) {
      capture('mobile_app_banner_shown', { authenticated: isAuth });
    }
  }, [isAuth, visible]);

  if (!visible) return null;

  const dismiss = () => {
    capture('mobile_app_banner_dismissed', { authenticated: isAuth });
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 864e5));
    } catch (e) {
      // Storage can throw in private mode — dismiss for this session anyway.
    }
    setVisible(false);
  };

  const openApp = async () => {
    capture('mobile_app_banner_open_clicked', {
      authenticated: isAuth,
      companion: isCompanion,
    });
    const platform = getMobilePlatform();

    const openWithStoreFallback = appUrl => {
      let fallbackTimer;
      const startedAt = Date.now();

      const cleanup = () => {
        window.clearTimeout(fallbackTimer);
        window.removeEventListener('blur', cleanup);
        window.removeEventListener('pagehide', cleanup);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
      const onVisibilityChange = () => {
        if (document.hidden) cleanup();
      };

      // iOS does not consistently emit `visibilitychange` before suspending
      // Safari during a custom-scheme handoff. `blur` catches that transition,
      // while the elapsed-time guard prevents a suspended timer from opening
      // the App Store when Safari eventually resumes.
      window.addEventListener('blur', cleanup);
      window.addEventListener('pagehide', cleanup);
      document.addEventListener('visibilitychange', onVisibilityChange);
      fallbackTimer = window.setTimeout(() => {
        const elapsed = Date.now() - startedAt;
        cleanup();
        const pageIsInactive =
          document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus());
        const timerWasSuspended = elapsed > STORE_FALLBACK_DELAY_MS + STORE_FALLBACK_MAX_DRIFT_MS;
        if (!platform || pageIsInactive || timerWasSuspended) return;
        capture('mobile_app_banner_store_fallback_opened', { platform });
        window.location.href = STORE_URLS[platform];
      }, STORE_FALLBACK_DELAY_MS);

      try {
        window.location.href = appUrl;
      } catch (e) {
        // Some embedded browsers reject unknown custom schemes synchronously;
        // keep the already-scheduled store fallback active.
      }
    };

    // A custom scheme can launch the app from a page already browsing the same
    // Universal-Link domain. If the app does not take the page to the
    // background, the timer opens the platform store instead.
    if (isCompanion) {
      openWithStoreFallback(MOBILE_COMPANION_SCHEME);
      return;
    }

    if (!isAuth) {
      openWithStoreFallback(MOBILE_APP_SCHEME);
      return;
    }

    // Authenticated: ask the backend for a one-time magic link that auto-logs-in.
    setLoading(true);
    try {
      const res = await getMagicLink(token);
      const magicUrl = new URL(res.data.url);
      const magicToken = magicUrl.searchParams.get('token');
      const appUrl = magicToken
        ? `${MOBILE_APP_SCHEME}?token=${encodeURIComponent(magicToken)}`
        : MOBILE_APP_SCHEME;
      openWithStoreFallback(appUrl);
    } catch (e) {
      // Minting failed — still try the app logged out, with the same store
      // fallback instead of blocking the user.
      openWithStoreFallback(MOBILE_APP_SCHEME);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.bar}>
      <img src={LOGO} alt="Neo Network" style={styles.logo} />
      <div style={styles.text}>
        <span style={styles.title}>Neo Network</span>
        <span style={styles.subtitle}>
          {isAuth
            ? 'Ouvrir l’app et rester connecté'
            : isCompanion
            ? 'Continuer en mode compagnon'
            : 'Ouvrir l’app'}
        </span>
      </div>
      <Button
        onClick={openApp}
        disabled={loading}
        variant="contained"
        size="small"
        style={styles.cta}
      >
        {loading ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Ouvrir'}
      </Button>
      <IconButton onClick={dismiss} size="small" aria-label="Fermer" style={styles.close}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

const styles = {
  bar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
    background: '#ffffff',
    borderTop: '1px solid #e0e0e0',
    boxShadow: '0 -2px 6px rgba(0,0,0,0.08)',
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    objectFit: 'contain',
    flexShrink: 0,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cta: {
    flexShrink: 0,
    textTransform: 'none',
    borderRadius: 20,
    minWidth: 72,
  },
  close: {
    flexShrink: 0,
    color: '#999',
  },
};

export default MobileAppBanner;
